import { useContext, useState } from "react";
import {
  Card,
  AuthContext,
  ViewContext,
  Loader,
  useAPI
} from "@/components/lib";
import { DropCard } from "@/components/dropCard/dropCard";
import { useForm } from "react-hook-form";
import { State } from "country-state-city";
import axios from "axios";
import Styles from "@/css/mainStyles";
import ImagesUpload from "@/components/form/file/newFile";

const PlayerModal = ({ setShow, formData, reload }: any) => {
  const viewContext = useContext<any>(ViewContext);
  const authContext = useContext<any>(AuthContext);
  const filesURL = "https://ghs-general.s3.us-east-1.amazonaws.com/";
  const profile_image = formData.profile_pic
    ? [{ type: "image", url: filesURL + formData.profile_pic }]
    : [];
  const galleryFiles =
    formData.gallery?.length > 0
      ? formData.gallery.map((file: any) => ({
          type: file.format,
          url: filesURL + file.url
        }))
      : [];

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const { data: coachesData } = useAPI(
    `${authContext.remote_server}/api/coach/admin`
  );

  const coachesOptions =
    coachesData?.data?.length > 0
      ? coachesData.data.map((coach: any) => ({
          label: coach.first_name + " " + coach.last_name,
          value: coach.id
        }))
      : [];

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      heightFeet: formData.info?.height?.feet || NaN,
      heightInches: formData.info?.height?.inches || NaN,
      weight: formData.info?.weight || "",
      fourty: formData.info?.fourty || "",
      agility: formData.info?.agility || "",
      jumpFeet: formData.info?.jump?.feet || NaN,
      jumpInches: formData.info?.jump?.inches || NaN,
      vertical: formData.info?.vertical?.feet || NaN,
      bench: formData.info?.bench || "",
      squat: formData.info?.squat || "",
      clean: formData.info?.clean || "",
      gpa: formData.info?.gpa || "",
      sat: formData.info?.sat || "",
      act: formData.info?.act || "",
      recognition: formData.info?.recognition || "",
      offers: formData.info?.offers || "",
      school: formData.info?.school,
      city: formData.info?.city,
      state: formData.info?.state,
      coach: formData.info?.coach_id,
      ...formData
    }
  });

  const validateFileSize = (blob, maxSizeMB, errorMessage) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (blob.size > maxSizeBytes) {
      setFormError(errorMessage);
      viewContext.notification.show(errorMessage, "error");
      throw new Error(errorMessage);
    }
    return true;
  };

  const onSubmit = async (data: any) => {
    setFormError("");
    const height = { feet: data.heightFeet, inches: data.heightInches };
    const jump = { feet: data.jumpFeet, inches: data.jumpInches };

    const info = {
      height,
      weight: data.weight,
      position: data.position,
      class: data.class,
      fourty: data.fourty,
      agility: data.agility,
      jump,
      vertical: { feet: data.vertical },
      bench: data.bench,
      squat: data.squat,
      clean: data.clean,
      gpa: data.gpa,
      sat: data.sat,
      act: data.act,
      recognition: data.recognition,
      school: data.school,
      city: data.city,
      state: data.state,
      coach_id: data.coach,
      offers: data.offers
    };

    const formPlayerData = new FormData();
    formPlayerData.append("first_name", data.first_name);
    formPlayerData.append("last_name", data.last_name);
    formPlayerData.append("email", data.email);
    formPlayerData.append("shirt", data.shirt);
    formPlayerData.append("info", JSON.stringify(info));
    formPlayerData.append("twitter_id", data.twitter_id || "");
    formPlayerData.append("hudl", data.hudl || "");

    if (data.profile_pic?.[0]?.url?.includes("blob")) {
      try {
        const response = await fetch(data.profile_pic[0].url);
        const blob = await response.blob();

        if (
          !validateFileSize(
            blob,
            5,
            "Profile Picture can’t be bigger than 5MB."
          )
        ) {
          return;
        }

        const file = new File([blob], "GHS_player_image", {
          type: blob.type
        });
        formPlayerData.append("profile_pic", file);
      } catch (error) {
        console.error(error);
      }
    }

    if (data.gallery?.length > 0) {
      if (data.gallery.length > 8) {
        return setFormError("Maximum of 8 files allowed.");
      }
      for (const file of data.gallery) {
        if (file.url.includes("blob")) {
          try {
            const response = await fetch(file.url);
            const blob = await response.blob();
            const originalFileName = file.url.split("/").pop();
            const maxSizeMB = file.type === "video" ? 100 : 10;

            if (
              !validateFileSize(
                blob,
                maxSizeMB,
                file.type === "video"
                  ? "A video can’t be bigger than 100MB."
                  : "Gallery Picture can’t be bigger than 10MB."
              )
            )
              return;

            const newFile = new File([blob], originalFileName, {
              type: blob.type
            });
            formPlayerData.append(file.type, newFile);
          } catch (error) {
            console.error(error);
          }
        }
      }
    }

    const createurl = `${authContext.remote_server}/api/player/new`;
    const editurl = `${authContext.remote_server}/api/player/${formData.id}`;

    try {
      setLoading(true);
      await axios({
        method: formData.id ? "patch" : "post",
        url: formData.id ? editurl : createurl,
        data: formPlayerData,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      viewContext.notification.show(
        "Player " + (formData.id ? "updated" : "created"),
        "success"
      );
      reload && reload();
    } catch (error) {
      console.error(error);
      viewContext.notification.show(error.message, "error");
    } finally {
      setLoading(false);
      setShow(false);
    }
  };

  const positionOptions = [
    { value: "QB", label: "QB" }, // secondLabel: "Quarterback" },
    { value: "RB", label: "RB" }, // secondLabel: "Running Back" },
    { value: "SLOT", label: "SLOT" }, // secondLabel: "Slot Receiver" },
    { value: "WR", label: "WR" }, // secondLabel: "Wide Receiver" },
    { value: "TE", label: "TE" }, // secondLabel: "Tight End" },
    { value: "OL", label: "OL" }, // secondLabel: "Offensive Lineman" },
    { value: "DL", label: "DL" }, // secondLabel: "Defensive Lineman" },
    { value: "OLB", label: "OLB" }, // secondLabel: "Outside Linebacker" },
    { value: "ILB", label: "ILB" }, // secondLabel: "Inside Linebacker" },
    { value: "LB", label: "LB" }, // secondLabel: "Linebacker" },
    { value: "DE", label: "DE" }, // secondLabel: "Defensive End" },
    { value: "SAF", label: "SAF" }, // secondLabel: "Safety" },
    { value: "DB", label: "DB" }, // secondLabel: "Defensive Back" },
    { value: "K/P", label: "K/P" }, // secondLabel: "Kicker/Punter" },
    { value: "ATH", label: "ATH" } // secondLabel: "Athlete" }
  ];

  const states = State.getStatesOfCountry("US");
  let statesOptions = [];
  for (const state of states)
    statesOptions.push({ value: state.name, label: state.name });

  const handleRemoveFile = async (file: any, index: number) => {
    try {
      if (!file.url.includes("blob")) {
        const fileURLDelete = file.url.split(filesURL)[1];
        await axios({
          method: "delete",
          url:
            authContext.remote_server +
            "/api/player/gallery/remove/" +
            fileURLDelete
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Card className="rounded-2xl w-11/12 md:w-auto">
      <div className="flex justify-between items-start">
        <h1 className="font-bold text-3xl text-black">
          {formData.id ? "Edit" : "Create"} Player
        </h1>
        <button onClick={() => setShow(false)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <form
        className="flex flex-col gap-2 mt-4 pr-2 h-[600px] md:w-[600px] overflow-y-scroll"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex justify-between items-center gap-4">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              First Name
            </label>
            <input
              placeholder="First Name"
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("first_name", { required: true })}
              style={errors.first_name && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Last Name
            </label>
            <input
              placeholder="Last Name"
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("last_name", { required: true })}
              style={errors.last_name && { border: "1px solid red" }}
            />
          </div>
        </div>

        <div className="flex justify-between items-center gap-4">
          <div className="flex flex-col w-1/6">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Number
            </label>
            <input
              placeholder="1"
              min={0}
              max={99}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("shirt", { required: true })}
              style={errors.shirt && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-5/6">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Email
            </label>
            <input
              placeholder="Email@mail.com"
              type="email"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("email", { required: true })}
              style={errors.email && { border: "1px solid red" }}
            />
          </div>
        </div>

        <div className="flex justify-between items-center gap-4">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Position
            </label>

            <select
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300 relative z-10"
              {...register("position")}
              style={errors.position && { border: "1px solid red" }}
            >
              <option value="">Select</option>
              {positionOptions.map((position) => (
                <option key={position.value} value={position.value}>
                  {position.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Class
            </label>
            <input
              placeholder="2030"
              min={1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("class")}
              style={errors.class && { border: "1px solid red" }}
            />
          </div>
        </div>

        <div className="flex justify-between items-center gap-4">
          <div className="flex flex-col w-3/5">
            <p className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Height
            </p>
            <div className="flex justify-between items-center gap-4">
              <input
                placeholder="Feet"
                type="number"
                min={0}
                max={9}
                className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                {...register("heightFeet")}
                style={errors.heightFeet && { border: "1px solid red" }}
              />
              <input
                placeholder="Inches"
                type="number"
                min={0}
                max={11}
                className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                {...register("heightInches")}
                style={errors.heightInches && { border: "1px solid red" }}
              />
            </div>
          </div>
          <div className="flex flex-col w-2/5">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Weight (lbs)
            </label>
            <input
              placeholder="Weight"
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("weight")}
              style={errors.weight && { border: "1px solid red" }}
            />
          </div>
        </div>
        <input type="hidden" {...register("profile_pic")} />
        <input type="hidden" {...register("gallery")} />

        <DropCard title="Profile Picture">
          <ImagesUpload
            onChange={(file: any) => setValue("profile_pic", file)}
            defaultFiles={profile_image}
          />
        </DropCard>
        <DropCard title="Strength & Speed">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Pro Agility
            </label>
            <input
              placeholder=""
              min={1}
              step={0.01}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("agility")}
              style={errors.agility && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              40 Time
            </label>
            <input
              placeholder=""
              min={1}
              step={0.001}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("fourty")}
              style={errors.fourty && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <p className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Broad Jump
            </p>
            <div className="flex justify-between items-center gap-4">
              <input
                placeholder="Feet"
                type="number"
                min={0}
                max={9}
                className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                {...register("jumpFeet")}
                style={errors.jumpFeet && { border: "1px solid red" }}
              />
              <input
                placeholder="Inches"
                type="number"
                min={0}
                max={11}
                className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
                {...register("jumpInches")}
                style={errors.jumpInches && { border: "1px solid red" }}
              />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Vertical
            </label>
            <input
              placeholder="Inches"
              type="number"
              min={0}
              step={0.01}
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("vertical")}
              style={errors.vertical && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Bench Max
            </label>
            <input
              placeholder=""
              min={1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("bench")}
              style={errors.bench && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Squat Max
            </label>
            <input
              placeholder=""
              min={1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("squat")}
              style={errors.squat && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Clean Max
            </label>
            <input
              placeholder=""
              min={1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("clean")}
              style={errors.clean && { border: "1px solid red" }}
            />
          </div>
        </DropCard>
        <DropCard title="Academics">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              GPA
            </label>
            <input
              placeholder=""
              min={0}
              max={6}
              step={0.1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("gpa")}
              style={errors.gpa && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              SAT Score
            </label>
            <input
              placeholder=""
              min={1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("sat")}
              style={errors.sat && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              ACT Score
            </label>
            <input
              placeholder=""
              min={1}
              type="number"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("act")}
              style={errors.act && { border: "1px solid red" }}
            />
          </div>
        </DropCard>
        <DropCard title="Gallery">
          <ImagesUpload
            maxFiles={8}
            onChange={(files: any) => setValue("gallery", files)}
            defaultFiles={galleryFiles}
            onDelete={(file, index) => handleRemoveFile(file, index)}
            accept="image/*, video/*"
          />
        </DropCard>
        <DropCard title="Stats & Recognition">
          <textarea
            placeholder="Enter text here..."
            className="px-[18px] py-2 lg:py-3 rounded-xl lg:text-lg w-full text-black border border-slate-300"
            {...register("recognition")}
            style={errors.recognition && { border: "1px solid red" }}
          />
        </DropCard>
        <DropCard title="College Offers">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Separate offers by a comma or a semicolon.
            </label>
            <textarea
              placeholder="Enter text here..."
              className="px-[18px] py-2 lg:py-3 rounded-xl lg:text-lg w-full text-black border border-slate-300"
              {...register("offers")}
              style={errors.offers && { border: "1px solid red" }}
            />
          </div>
        </DropCard>
        <DropCard title="Contact">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              High School
            </label>
            <input
              placeholder=""
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("school")}
              style={errors.school && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              City
            </label>
            <input
              placeholder=""
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("city")}
              style={errors.city && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              State
            </label>
            <select
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("state")}
              style={errors.state && { border: "1px solid red" }}
            >
              <option>Select</option>
              {statesOptions.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Coach
            </label>
            <select
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("coach")}
              style={errors.coach && { border: "1px solid red" }}
            >
              <option value={""}>Select</option>
              {coachesOptions.map((coach) => (
                <option key={coach.value} value={coach.value}>
                  {coach.label}
                </option>
              ))}
            </select>
          </div>
        </DropCard>
        <DropCard title="Follow me">
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              X (username)
            </label>
            <input
              placeholder="X username"
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("twitter_id")}
              style={errors.twitter_id && { border: "1px solid red" }}
            />
          </div>
          <div className="flex flex-col w-full">
            <label className="text-sm lg:text-lg font-medium text-black ml-2 mb-2">
              Hudl (account number)
            </label>
            <input
              placeholder="Hudl account number"
              type="text"
              className="px-[18px] py-2 lg:py-3 rounded-full lg:text-lg w-full text-black border border-slate-300"
              {...register("hudl")}
              style={errors.hudl && { border: "1px solid red" }}
            />
          </div>
        </DropCard>

        {formError && (
          <p className="text-red-500 text-right mt-2 mr-2">{formError}</p>
        )}

        <div className="flex justify-end gap-4 ml-auto my-4 w-1/2">
          <button
            type="button"
            className={Styles.cancelButton}
            onClick={() => setShow(false)}
          >
            Cancel
          </button>

          <button type="submit" className={Styles.submitButton}>
            {formData.id ? "Update" : "Create"}
            {loading && <Loader />}
          </button>
        </div>
      </form>
    </Card>
  );
};

export default PlayerModal;
